// trading-dashboard-utils.tsx
import { Button } from '@/components/ui/button';
import {
  CandlestickChart,
  LineChart,
  ListOrdered,
  Menu,
  Search,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  IChartingLibraryWidget,
  LibrarySymbolInfo,
  ResolutionString,
} from '@/public/charting_library-master/charting_library/charting_library';
import { SimpleDatafeed } from './tradingview-utils/SimpleDatafeed';
import { SymbolInfo, WatchData } from './tradingview-utils/types';
import { SearchSymbolResultItem } from '@/public/charting_library-master/charting_library/charting_library';
import AuthScreen from './auth-screen';
import { Socket } from 'socket.io-client';
import io from 'socket.io-client'; // Make sure socket.io-client is installed
export const globalDatafeedInstance = new SimpleDatafeed();

export const datafeedRequestTracker = {
  activeRequests: new Set<string>(),
  addRequest(symbol: string, resolution: string): string {
    const requestId = `${symbol}_${resolution}_${Date.now()}`;
    this.activeRequests.add(requestId);
    return requestId;
  },
  removeRequest(requestId: string): void {
    this.activeRequests.delete(requestId);
  },
  getActiveRequestCount(): number {
    return this.activeRequests.size;
  },
  hasActiveRequests(symbol?: string): boolean {
    if (!symbol) return this.activeRequests.size > 0;
    return Array.from(this.activeRequests).some((id: string) =>
      id.startsWith(`${symbol}_`)
    );
  },
};

export const dataRangeCache = {
  cache: new Map<string, { data: any; timestamp: number }>(),

  get(symbol: string, source: string): any | null {
    const key = `${symbol}_${source}_range`;
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Cache for 5 minutes
    if (Date.now() - entry.timestamp > 300000) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  },

  set(symbol: string, source: string, data: any): void {
    const key = `${symbol}_${source}_range`;
    this.cache.set(key, { data, timestamp: Date.now() });
  },
};

export const chartDataCache = {
  cache: new Map<string, { data: any; timestamp: number }>(),

  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Check if entry is still valid (5 minutes)
    if (Date.now() - entry.timestamp > 300000) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  },

  set(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  },

  invalidate(keyPattern: string): void {
    // Remove entries matching pattern
    for (const key of this.cache.keys()) {
      if (key.includes(keyPattern)) {
        this.cache.delete(key);
      }
    }
  },
};

export const formatSymbolConsistently = (model: any): string => {
  // If model already has a properly formatted symbol
  if (model.name && model.name.includes(':')) {
    return model.name;
  }

  // Handle WatchInspect prefix consistently
  const brand =
    model.exchange === 'WatchInspect' ? 'Rolex' : model.exchange || 'Rolex';

  // Clean model description (remove brand name if present)
  const cleanDescription = (desc: string) => {
    if (!desc) return '';
    return desc.replace(`${brand} `, '').replace(/\s+/g, ' ').trim();
  };

  // Determine the most reliable model identifier
  let modelIdentifier = '';
  if (model.symbol && !model.symbol.includes(':')) {
    modelIdentifier = model.symbol;
  } else if (model.description) {
    modelIdentifier = cleanDescription(model.description);
  } else if (model.subModel) {
    modelIdentifier = model.subModel;
  } else {
    // Fallback to INDEX if nothing else is available
    return 'Rolex:INDEX';
  }

  return `${brand}:${modelIdentifier}`;
};

export const getGlobalSocket = (() => {
  let socket: Socket | null = null;

  return () => {
    if (!socket) {
      const socketUrl = 'http://localhost:3004';
      socket = io(socketUrl, {
        path: '/api/socketio',
        transports: ['websocket'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 20000,
      });

      socket.on('connect', () => {
        console.log('Global socket connected successfully:', socket?.id);
      });

      socket.on('connect_error', (error) => {
        console.error('Global socket connection error:', error.message);
      });
    }

    return socket;
  };
})();

type PageName = 'charts' | 'tracker' | 'dashboard';

interface SidebarProps {
  pageName: PageName;
}

interface MobileHeaderProps {
  cinzel: any;
  SidebarDemo: React.ComponentType<SidebarProps>;
}

export interface ChartState {
  symbol: string | null;
  interval: string | null;
  chartType: number | null;
}

interface TradingViewInitProps {
  container: HTMLDivElement;
  watchData: any[];
  setSelectedSymbol: (symbol: SymbolInfo) => void;
  setIsLoading: (loading: boolean) => void;
  onLoadComplete?: () => void;
  savedState?: ChartState;
}

export const createCustomDatafeed = () => {
  return globalDatafeedInstance;
};

export const loadWatchData = async () => {
  try {
    const response = await fetch('/api/trading-dashboard/watchData');
    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error loading watch data:', error);
    return [];
  }
};

export const handleSymbolSelect = (
  symbol: string,
  setSelectedSymbol: (symbol: SymbolInfo) => void,
  tvWidget: IChartingLibraryWidget | null,
  chartStateRef?: React.MutableRefObject<ChartState>,
  watchData?: WatchData[]
): SymbolInfo | undefined => {
  console.log('watchData in handleSymbolSelect:', watchData);
  if (tvWidget) {
    const currentInterval = tvWidget.symbolInterval().interval;
    tvWidget.setSymbol(symbol, currentInterval, () => {
      console.log(`Chart updated to symbol: ${symbol}`);

      const activeChart = tvWidget.activeChart();
      const currentPrice = (activeChart as any).crosshairPrice?.() || 0;

      const selectedWatchDataItem = watchData?.find(
        (item) => item.symbol === symbol
      );
      console.log('selectedWatchDataItem:', selectedWatchDataItem);

      let symbolInfo: SymbolInfo = { name: symbol, price: currentPrice };
      if (selectedWatchDataItem) {
        symbolInfo = {
          name: symbol,
          price: currentPrice,
          exchange: selectedWatchDataItem.exchange,
          description: selectedWatchDataItem.description,
        };
      }

      console.log('SymbolInfo being set:', symbolInfo);

      setSelectedSymbol(symbolInfo);

      if (chartStateRef) {
        chartStateRef.current = {
          symbol: symbol,
          interval: currentInterval,
          chartType: tvWidget.chart().chartType(),
        };
      }
      return symbolInfo;
    });
  }
  return undefined;
};
export const saveChartState = (
  widget: IChartingLibraryWidget,
  chartStateRef: React.MutableRefObject<ChartState>
) => {
  try {
    const symbolInterval = widget.symbolInterval();
    chartStateRef.current = {
      symbol: symbolInterval.symbol,
      interval: symbolInterval.interval,
      chartType: widget.chart().chartType(),
    };
    console.log('Chart state saved:', chartStateRef.current);
  } catch (error) {
    console.error('Error saving chart state:', error);
  }
};
export const restoreChartState = (
  widget: IChartingLibraryWidget,
  chartStateRef: React.MutableRefObject<ChartState>
) => {
  try {
    const { symbol, interval, chartType } = chartStateRef.current;
    if (symbol && interval) {
      widget.setSymbol(symbol, interval as ResolutionString, () => {
        if (chartType !== null) {
          widget.chart().setChartType(chartType);
        }
        console.log('Chart state restored:', chartStateRef.current);
      });
    }
  } catch (error) {
    console.error('Error restoring chart state:', error);
  }
};
export const initializeTradingViewWidget = async ({
  container,
  watchData,
  setSelectedSymbol,
  setIsLoading,
  onLoadComplete,
  savedState,
}: TradingViewInitProps): Promise<IChartingLibraryWidget> => {
  const currentWatchData =
    watchData.length > 0 ? watchData : await loadWatchData();
  if (!currentWatchData.length) {
    throw new Error('No watch data available');
  }

  const defaultSymbol = savedState?.symbol || 'Rolex:16610';
  setSelectedSymbol({ name: defaultSymbol });

  const isMobile = window.innerWidth < 768;

  const widget = new window.TradingView.widget({
    symbol: defaultSymbol,
    interval: (savedState?.interval || '1W') as ResolutionString,
    container: container,
    datafeed: globalDatafeedInstance,
    library_path: '/charting_library-master/charting_library/',
    locale: 'en',
    disabled_features: [
      'use_localstorage_for_settings' as const,
      'header_screenshot' as const,
      'timeframes_toolbar' as const,
      ...(isMobile ? ['left_toolbar' as const] : []),
    ],
    enabled_features: [
      'show_exchange_logos' as const,
      'header_symbol_search' as const,
      'symbol_search_hot_key' as const,
      'header_chart_type' as const,
      'header_settings' as const,
      'header_indicators' as const,
      'header_compare' as const,
      'header_fullscreen_button' as const,
    ],
    theme: 'dark',
    fullscreen: false,
    autosize: true,
    overrides: {
      // Chart style
      'mainSeriesProperties.style': savedState?.chartType || 3,
      'mainSeriesProperties.showPriceLine': true,

      // Price axis properties
      'mainSeriesProperties.priceAxisProperties.autoScale': true,
      'mainSeriesProperties.priceAxisProperties.logarithmic': false,
      'mainSeriesProperties.priceAxisProperties.alignLabels': true,

      // Background and grid
      'paneProperties.backgroundType': 'solid',
      'paneProperties.background': '#0f172a',
      'paneProperties.vertGridProperties.color': '#1e293b',
      'paneProperties.horzGridProperties.color': '#1e293b',
      'paneProperties.vertGridProperties.style': 0,
      'paneProperties.horzGridProperties.style': 0,
      'paneProperties.vertGridProperties.width': 1,
      'paneProperties.horzGridProperties.width': 1,

      // Legend properties for mobile
      ...(isMobile && {
        'paneProperties.legendProperties.showSeriesTitle': true,
        'paneProperties.legendProperties.showLegend': true,
        'paneProperties.legendProperties.showBarChange': true,
        'paneProperties.legendProperties.showBackground': true,
        'paneProperties.leftMargin': 5,
        'paneProperties.rightMargin': 5,
        'priceScale.alignLabels': true,
        'priceScale.borderVisible': false,
      }),

      // Rest of the overrides remain the same...
      'paneProperties.crossHairProperties.color': '#64748b',
      'paneProperties.crossHairProperties.width': 1,
      'paneProperties.crossHairProperties.style': 2,
      'scalesProperties.textColor': '#94a3b8',
      'scalesProperties.lineColor': '#334155',
      'scalesProperties.fontSize': isMobile ? 10 : 12,
      'mainSeriesProperties.candleStyle.upColor': '#10b981',
      'mainSeriesProperties.candleStyle.downColor': '#ef4444',
      'mainSeriesProperties.candleStyle.drawWick': true,
      'mainSeriesProperties.candleStyle.drawBorder': true,
      'mainSeriesProperties.candleStyle.borderUpColor': '#10b981',
      'mainSeriesProperties.candleStyle.borderDownColor': '#ef4444',
      'mainSeriesProperties.candleStyle.wickUpColor': '#10b981',
      'mainSeriesProperties.candleStyle.wickDownColor': '#ef4444',
      'mainSeriesProperties.priceFormat.precision': 2,
      'mainSeriesProperties.priceFormat.minMove': 0.01,
      'paneProperties.legendProperties.showLegend': true,
      'paneProperties.legendProperties.showSeriesTitle': true,
      'paneProperties.legendProperties.showSeriesOHLC': true,
      'paneProperties.legendProperties.showBarChange': true,
      'paneProperties.legendProperties.showBackground': true,
      'paneProperties.legendProperties.fontSize': isMobile ? 10 : 12,
      'paneProperties.legendProperties.backgroundTransparency': 0,
      'paneProperties.legendProperties.color': '#e2e8f0',
      'symbolWatermarkProperties.transparency': 80,
      'symbolWatermarkProperties.color': 'rgba(51, 65, 85, 0.5)', // slightly transparent
      volumePaneSize: 'medium',
      'paneProperties.topMargin': 20,
      'paneProperties.bottomMargin': 8,
      'timeScale.rightOffset': 12,
      'timeScale.leftOffset': 12,
      'timeScale.fontSize': isMobile ? 10 : 12,
      'timeScale.borderColor': '#334155',
      navigationButtonsVisibility: 'adaptive',
      'handles.backgroundColor': '#1e293b',
      'handles.hover.backgroundColor': '#334155',
      'priceScale.borderColor': '#334155',
      'priceScale.backgroundColor': '#0f172a',
      'priceScale.fontSize': isMobile ? 10 : 12,
      'priceScale.fontFamily': 'Inter, -apple-system, system-ui, sans-serif',
      'plot.lineWidth': 2,
      'plot.plotBackground': '#0f172a',
      'plot.backgroundColor': '#0f172a',
      'statusLine.style': isMobile ? 1 : 2,
      'statusLine.fontSize': isMobile ? 10 : 12,
      'sessions.vertlines.visible': false,
      'chart.animations': true,
    },
    width: container.clientWidth,
    height: container.clientHeight,
    loading_screen: { backgroundColor: '#131722' },
    custom_css_url: isMobile ? '/tradingview-mobile.css' : undefined,
  });

  widget.onChartReady(() => {
    setIsLoading(false);
    onLoadComplete?.();
  });

  return widget;
};
export const MobileNav = ({
  activeComponent,
  setActiveComponent,
}: {
  activeComponent: string;
  setActiveComponent: (
    component: 'chart' | 'transactions' | 'watchlist'
  ) => void;
}) => (
  <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 p-2 z-50">
    <div className="flex justify-around items-center">
      <Button
        variant="ghost"
        className={`flex flex-col items-center p-2 ${
          activeComponent === 'chart' ? 'text-blue-400' : 'text-gray-400'
        }`}
        onClick={() => setActiveComponent('chart')}
      >
        <CandlestickChart size={28} />
        <span className="text-sm mt-1">Chart</span>
      </Button>
      <Button
        variant="ghost"
        className={`flex flex-col items-center p-2 ${
          activeComponent === 'transactions' ? 'text-blue-400' : 'text-gray-400'
        }`}
        onClick={() => setActiveComponent('transactions')}
      >
        <LineChart size={28} />
        <span className="text-sm mt-1">Transactions</span>
      </Button>
      <Button
        variant="ghost"
        className={`flex flex-col items-center p-2 ${
          activeComponent === 'watchlist' ? 'text-blue-400' : 'text-gray-400'
        }`}
        onClick={() => setActiveComponent('watchlist')}
      >
        <ListOrdered size={28} />
        <span className="text-sm mt-1">Watchlist</span>
      </Button>
    </div>
  </div>
);
export const MobileHeader = ({ cinzel, SidebarDemo }: MobileHeaderProps) => (
  <div className="fixed top-0 left-0 right-0 bg-slate-900 border-b border-slate-800 p-4 z-50">
    <div className="flex justify-between items-center">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon">
            <Menu className="h-7 w-7 text-gray-400" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-[80%] sm:w-[385px] bg-slate-900 text-white p-0"
        >
          <SidebarDemo pageName="dashboard" />
        </SheetContent>
      </Sheet>
      <h1 className={`${cinzel.className} text-xl font-semibold text-white`}>
        Watch Inspect
      </h1>
      <Button variant="ghost" size="icon">
        <Search className="h-7 w-7 text-gray-400" />
      </Button>
    </div>
  </div>
);
export const renderAuthScreen = (props?: {
  handleGuestAccess?: () => void;
}) => {
  // Provide a default empty function if handleGuestAccess is not provided
  const handleGuestAccess = props?.handleGuestAccess || (() => {});
  return <AuthScreen handleGuestAccess={handleGuestAccess} />;
};

export interface DataRange {
  firstDataPoint: number;
  lastDataPoint: number;
  totalDataPoints: number;
}

export interface SymbolDataRange {
  [key: string]: {
    firstDataPoint: number;
    lastDataPoint: number;
  };
}

export const formatChartDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const getWeekStartTime = (timestamp: number): number => {
  const date = new Date(timestamp);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() - date.getUTCDay() + 1);
  return date.getTime();
};

export const validateWatchData = (data: any[]): boolean => {
  if (!Array.isArray(data) || data.length === 0) {
    console.error('Invalid or empty watch data');
    return false;
  }
  return true;
};

export const handleAsyncError = (error: any, message: string) => {
  console.error(message, error);
  throw new Error(message);
};
